import React, { useState, useEffect } from 'react'
import { X, Mail, Phone, User, MapPin, Shield } from 'lucide-react'
import { supabase, SecretUser } from '../../lib/supabase'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface EditUserModalProps {
  user: SecretUser
  onClose: () => void
  onSuccess: () => void
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    phone: user.phone || '',
    zip_code: user.zip_code || '',
    email_verified: user.email_verified,
    phone_verified: user.phone_verified,
    is_active: user.is_active,
  })
  const [isAdmin, setIsAdmin] = useState(false)
  const [checkingAdmin, setCheckingAdmin] = useState(true)

  useEffect(() => {
    checkAdminStatus()
  }, [user.id])

  const checkAdminStatus = async () => {
    try {
      setCheckingAdmin(true)
      const { data, error } = await supabase
        .from('secret_admin_users')
        .select('role')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') throw error

      setIsAdmin(!!data)
    } catch (error) {
      console.error('Failed to check admin status:', error)
    } finally {
      setCheckingAdmin(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setIsLoading(true)

      // Update user details
      const { error: updateError } = await supabase
        .from('secret_users')
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          zip_code: formData.zip_code,
          email_verified: formData.email_verified,
          phone_verified: formData.phone_verified,
          is_active: formData.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id)

      if (updateError) throw updateError

      // Handle admin role
      const { data: existingAdmin, error: adminCheckError } = await supabase
        .from('secret_admin_users')
        .select('id')
        .eq('user_id', user.id)
        .single()

      if (adminCheckError && adminCheckError.code !== 'PGRST116') {
        throw adminCheckError
      }

      if (isAdmin && !existingAdmin) {
        // Add admin role
        const { error: adminInsertError } = await supabase
          .from('secret_admin_users')
          .insert({
            user_id: user.id,
            role: 'admin',
            permissions: ['manage_events', 'manage_users', 'manage_orders', 'view_analytics'],
          })

        if (adminInsertError) throw adminInsertError

        // Create audit log
        await supabase
          .from('secret_audit_logs')
          .insert({
            event_type: 'admin_action',
            severity: 'warning',
            action: 'admin_role_assigned',
            user_id: user.id,
            metadata: {
              assigned_by: 'admin_user',
              role: 'admin',
            },
          })

      } else if (!isAdmin && existingAdmin) {
        // Remove admin role
        const { error: adminDeleteError } = await supabase
          .from('secret_admin_users')
          .delete()
          .eq('user_id', user.id)

        if (adminDeleteError) throw adminDeleteError

        // Create audit log
        await supabase
          .from('secret_audit_logs')
          .insert({
            event_type: 'admin_action',
            severity: 'warning',
            action: 'admin_role_removed',
            user_id: user.id,
            metadata: {
              removed_by: 'admin_user',
            },
          })
      }

      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type='success'
          title='User Updated'
          message='User information updated successfully'
        />
      ))
      onSuccess()
      onClose()

    } catch (error: any) {
      console.error('Failed to update user:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type='error'
          title='Update Failed'
          message={error.message || 'Failed to update user. Please try again.'}
        />
      ))
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground">Edit User</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Update user information and permissions
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>First Name</span>
                </div>
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="John"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <div className="flex items-center space-x-2">
                  <User size={16} />
                  <span>Last Name</span>
                </div>
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Doe"
              />
            </div>
          </div>

          {/* Contact Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <div className="flex items-center space-x-2">
                  <Mail size={16} />
                  <span>Email</span>
                </div>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <div className="flex items-center space-x-2">
                  <Phone size={16} />
                  <span>Phone</span>
                </div>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1234567890"
              />
            </div>
          </div>

          {/* Zip Code */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <div className="flex items-center space-x-2">
                <MapPin size={16} />
                <span>Zip Code</span>
              </div>
            </label>
            <input
              type="text"
              value={formData.zip_code}
              onChange={(e) => handleChange('zip_code', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="12345"
              maxLength={10}
            />
          </div>

          {/* Verification Status */}
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <h3 className="text-sm font-medium text-foreground mb-3">Verification Status</h3>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.email_verified}
                onChange={(e) => handleChange('email_verified', e.target.checked)}
                className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
              />
              <span className="text-sm text-foreground">Email Verified</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.phone_verified}
                onChange={(e) => handleChange('phone_verified', e.target.checked)}
                className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
              />
              <span className="text-sm text-foreground">Phone Verified</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
              />
              <span className="text-sm text-foreground">Account Active</span>
            </label>
          </div>

          {/* Admin Role */}
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="text-orange-600 mt-0.5" size={20} />
              <div className="flex-1">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <div className="font-medium text-foreground">Admin Access</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Grant this user administrator privileges
                    </div>
                  </div>
                  {checkingAdmin ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <input
                      type="checkbox"
                      checked={isAdmin}
                      onChange={(e) => setIsAdmin(e.target.checked)}
                      className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                    />
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Changes</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditUserModal
